/**
 * Shared P&L Profit calculation logic.
 *
 * Both the P&L page (app-financials.tsx) and the dashboard's "Last Month
 * Profit" KPI (app-dashboard.tsx) call `computePnlProfitForMonth` so that
 * they are guaranteed to produce the same number for any given month and
 * property scope. Any change to the Profit formula must be made in the
 * canonical `@shared/pnl-compute` module, which is also consumed server-side
 * by the Owner CRM financial roll-up so the two surfaces can never drift.
 */

import {
  computePnlTotalsForMonth,
  parsePadsplitBreakdown,
  type PnlExpenseRow,
  type PnlIncomeRow,
  type PnlCatSettings,
} from "@shared/pnl-compute";

// Re-export the shared types + notes parser so existing client imports
// (`@/lib/pnl-profit`) keep working unchanged.
export { parsePadsplitBreakdown };
export type { PnlExpenseRow, PnlIncomeRow, PnlCatSettings };

/**
 * Compute P&L Profit for a single month key (YYYY-MM).
 *
 * Thin wrapper over the canonical `computePnlTotalsForMonth` helper that
 * returns just the net income (Profit). This is the authoritative Profit
 * formula — identical to what the P&L page renders in the Profit row for each
 * month column.
 *
 * @param filteredExpenses  Expenses already scoped to the target properties
 *                          and with split parents excluded.
 * @param filteredIncome    Income rows already scoped to the target properties
 *                          and with split parents excluded.
 * @param catSettings       Expense category settings (for balance-sheet /
 *                          custom-expense-category handling). May be undefined
 *                          while the query is loading; the function treats it
 *                          as "no custom categories" in that case.
 * @param monthKey          YYYY-MM string for the target month.
 */
export function computePnlProfitForMonth(
  filteredExpenses: PnlExpenseRow[],
  filteredIncome: PnlIncomeRow[],
  catSettings: PnlCatSettings | undefined,
  monthKey: string,
): number {
  return computePnlTotalsForMonth(filteredExpenses, filteredIncome, catSettings, monthKey).netIncome;
}
